import React, { useEffect, useRef, useState } from 'react';
import {
  Modal,
  ModalDialog,
  DialogTitle,
  DialogContent,
  Input,
  List,
  ListItem,
  ListItemButton,
  Typography,
  Box,
  CircularProgress,
  Chip,
} from '@mui/joy';
import { MagnifyingGlass as Search, UsersThree as SupervisorAccount } from '@phosphor-icons/react';
import { apiClient } from '../../lib/api';
import { useImpersonation } from '../../contexts/ImpersonationContext';
import type { ImpersonationTarget } from '../../lib/impersonation';

interface PaginatedUsers {
  count: number;
  results: ImpersonationTarget[];
}

interface ImpersonateUserModalProps {
  open: boolean;
  onClose: () => void;
}

export const ImpersonateUserModal: React.FC<ImpersonateUserModalProps> = ({ open, onClose }) => {
  const { startImpersonation } = useImpersonation();
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<ImpersonationTarget[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [startingId, setStartingId] = useState<number | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!open) {
      setSearch('');
      setResults([]);
      setError(null);
      setStartingId(null);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      setError(null);
      try {
        const qs = search.trim() ? `?search=${encodeURIComponent(search.trim())}` : '';
        const res = await apiClient.get<PaginatedUsers>(`/api/impersonation/users/${qs}`);
        setResults(res.data.results || []);
      } catch {
        setError('Failed to load users. Super Admin privileges are required.');
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [search, open]);

  const handleSelect = async (target: ImpersonationTarget) => {
    setStartingId(target.id);
    setError(null);
    try {
      await startImpersonation(target);
      // startImpersonation triggers a full page reload on success.
    } catch {
      setError(`Could not start impersonating ${target.name}.`);
      setStartingId(null);
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog sx={{ width: 560, maxWidth: '95vw' }}>
        <DialogTitle>
          <SupervisorAccount style={{ marginRight: 8 }} />
          Impersonate a user
        </DialogTitle>
        <DialogContent>
          <Typography level="body-sm" sx={{ mb: 2 }}>
            Search across all organizations. You will act as the selected user
            until you exit impersonation.
          </Typography>

          <Input
            autoFocus
            placeholder="Search by name, email, or organization…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            startDecorator={<Search />}
            sx={{ mb: 2 }}
          />

          {error && (
            <Typography level="body-sm" color="danger" sx={{ mb: 1 }}>
              {error}
            </Typography>
          )}

          <Box sx={{ minHeight: 200, maxHeight: 380, overflowY: 'auto' }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size="sm" />
              </Box>
            ) : results.length === 0 ? (
              <Typography level="body-sm" sx={{ textAlign: 'center', py: 4, color: 'text.tertiary' }}>
                No users found.
              </Typography>
            ) : (
              <List>
                {results.map((u) => (
                  <ListItem key={u.id}>
                    <ListItemButton
                      onClick={() => handleSelect(u)}
                      disabled={startingId !== null}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography level="title-sm">{u.name}</Typography>
                        <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                          {u.email}
                        </Typography>
                      </Box>
                      <Chip size="sm" variant="soft" color="neutral">
                        {u.organization?.name || 'No organization'}
                      </Chip>
                      {startingId === u.id && <CircularProgress size="sm" sx={{ ml: 1 }} />}
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        </DialogContent>
      </ModalDialog>
    </Modal>
  );
};
